import type { WorkflowEdgeData } from "../_components/WorkflowEdge";
import type { Edge, Node } from "@xyflow/react";

export interface WorkflowGraphLookups {
  childrenMap: Map<string, string[]>;
  nodeMap: Map<string, Node>;
  onCompleteTargetIds: Set<string>;
  forEachNodes: Node[];
}

export function buildWorkflowGraphLookups(nodes: Node[], edges: Edge[]): WorkflowGraphLookups {
  const nodeMap = new Map(nodes.map(node => [node.id, node]));
  const childrenMap = new Map<string, string[]>();
  const onCompleteTargetIds = new Set<string>();

  for (const edge of edges) {
    const children = childrenMap.get(edge.source) || [];
    children.push(edge.target);
    childrenMap.set(edge.source, children);

    if ((edge.data as WorkflowEdgeData | undefined)?.isOnComplete) {
      onCompleteTargetIds.add(edge.target);
    }
  }

  const forEachNodes = nodes.filter(node => (node.data as Record<string, unknown>)?.type === "for-each");

  return {
    childrenMap,
    nodeMap,
    onCompleteTargetIds,
    forEachNodes,
  };
}

export function getDirectLoopBodyChildren(
  forEachNodeId: string,
  childrenMap: Map<string, string[]>,
  onCompleteTargetIds: Set<string>,
  { excludePlaceholders = false }: { excludePlaceholders?: boolean } = {},
): string[] {
  return (childrenMap.get(forEachNodeId) || []).filter(childId => {
    if (onCompleteTargetIds.has(childId)) {
      return false;
    }

    if (excludePlaceholders && childId.startsWith("placeholder-")) {
      return false;
    }

    return true;
  });
}

export function collectLoopBodyDescendantsForForEach(
  forEachNodeId: string,
  childrenMap: Map<string, string[]>,
  onCompleteTargetIds: Set<string>,
  { excludePlaceholders = false }: { excludePlaceholders?: boolean } = {},
): Set<string> {
  const directLoopBodyChildren = getDirectLoopBodyChildren(forEachNodeId, childrenMap, onCompleteTargetIds, {
    excludePlaceholders,
  });
  const loopBodyIds = new Set<string>(directLoopBodyChildren);
  const queue = [...directLoopBodyChildren];

  while (queue.length > 0) {
    const current = queue.shift()!;
    const children = childrenMap.get(current) || [];

    for (const childId of children) {
      if (excludePlaceholders && childId.startsWith("placeholder-")) {
        continue;
      }
      if (loopBodyIds.has(childId)) {
        continue;
      }

      loopBodyIds.add(childId);
      queue.push(childId);
    }
  }

  return loopBodyIds;
}

export function collectAllLoopBodyDescendantIds(
  forEachNodes: Node[],
  childrenMap: Map<string, string[]>,
  onCompleteTargetIds: Set<string>,
  { excludePlaceholders = false }: { excludePlaceholders?: boolean } = {},
): Set<string> {
  const loopBodyDescendantIds = new Set<string>();

  for (const forEachNode of forEachNodes) {
    const loopBodyIds = collectLoopBodyDescendantsForForEach(forEachNode.id, childrenMap, onCompleteTargetIds, {
      excludePlaceholders,
    });

    for (const loopBodyId of loopBodyIds) {
      loopBodyDescendantIds.add(loopBodyId);
    }
  }

  return loopBodyDescendantIds;
}
